import { useEffect } from 'react';
import { useI18n } from '@/hooks/useI18n';
import { useLocation } from 'react-router-dom';
import heroImageWebp from '@/assets/hero-flowers.webp';
import heroImageJpeg from '@/assets/hero-flowers.jpg';

interface SEOHeadProps {
  title?: string;
  description?: string;
  canonical?: string;
  jsonLd?: object;
}

export const SEOHead = ({ title, description, canonical, jsonLd }: SEOHeadProps) => {
  const { language, t } = useI18n();
  const location = useLocation();

  useEffect(() => {
    // Set consistent page title to "Andean Bloom" for all pages
    document.title = "Andean Bloom";

    // Preload critical hero images for better LCP when on home page
    if (location.pathname === '/') {
      // Check if preload links already exist
      if (!document.querySelector('link[rel="preload"][href*="hero-flowers"]')) {
        // Preload WebP hero image with high priority
        const webpPreload = document.createElement('link');
        webpPreload.rel = 'preload';
        webpPreload.as = 'image';
        webpPreload.href = heroImageWebp;
        webpPreload.type = 'image/webp';
        webpPreload.fetchPriority = 'high';
        document.head.appendChild(webpPreload);

        // Preload JPEG fallback for browsers without WebP support
        const jpegPreload = document.createElement('link');
        jpegPreload.rel = 'preload';
        jpegPreload.as = 'image';
        jpegPreload.href = heroImageJpeg;
        jpegPreload.type = 'image/jpeg';
        document.head.appendChild(jpegPreload);
      }
    }

    // Set meta description
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.setAttribute('name', 'description');
        document.head.appendChild(metaDescription);
      }
      metaDescription.setAttribute('content', description);
    }

    // Set canonical URL
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    // Add Open Graph tags
    const ogTags = [
      { property: 'og:title', content: 'Andean Bloom' },
      { property: 'og:description', content: description || 'Premium Ecuadorian roses, directly from selected farms to buyers worldwide.' },
      { property: 'og:type', content: 'website' },
      { property: 'og:url', content: window.location.href },
      { property: 'og:locale', content: 'en_US' },
      { property: 'og:site_name', content: 'Andean Bloom' },
      { property: 'og:image', content: 'https://andean-bloom.com/og-image.jpg' },
      { property: 'og:image:secure_url', content: 'https://andean-bloom.com/og-image.jpg' },
      { property: 'og:image:alt', content: 'Andean Bloom — premium Ecuadorian roses' },
    ];


    ogTags.forEach(tag => {
      if (tag.content) {
        let metaTag = document.querySelector(`meta[property="${tag.property}"]`);
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('property', tag.property);
          document.head.appendChild(metaTag);
        }
        metaTag.setAttribute('content', tag.content);
      }
    });

    // Add Twitter Card tags
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Andean Bloom' },
      { name: 'twitter:description', content: description || 'Premium Ecuadorian roses, directly from selected farms to buyers worldwide.' },
      { name: 'twitter:image', content: 'https://andean-bloom.com/og-image.jpg' },
    ];


    twitterTags.forEach(tag => {
      if (tag.content) {
        let metaTag = document.querySelector(`meta[name="${tag.name}"]`);
        if (!metaTag) {
          metaTag = document.createElement('meta');
          metaTag.setAttribute('name', tag.name);
          document.head.appendChild(metaTag);
        }
        metaTag.setAttribute('content', tag.content);
      }
    });

    // Add JSON-LD structured data
    if (jsonLd) {
      const existingJsonLd = document.querySelector('script[type="application/ld+json"]');
      if (existingJsonLd) {
        existingJsonLd.remove();
      }

      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // Organization schema (always present)
    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Andean Bloom",
      "description": "Leading flower trade company connecting Ecuador farms with Dubai buyers",
      "url": window.location.origin,
      "logo": `${window.location.origin}/logo.png`,
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Av. 6 de Diciembre N24-253",
        "addressLocality": "Quito",
        "addressCountry": "EC"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+593-2-XXX-XXXX",
        "contactType": "customer service",
        "areaServed": ["EC", "AE"],
        "availableLanguage": ["English", "Spanish"]
      },
      "sameAs": [
        "https://www.linkedin.com/company/andean-bloom",
        "https://www.facebook.com/andeanbloom"
      ]
    };

    if (!jsonLd) {
      const orgScript = document.createElement('script');
      orgScript.type = 'application/ld+json';
      orgScript.textContent = JSON.stringify(organizationSchema);
      document.head.appendChild(orgScript);
    }

  }, [title, description, canonical, jsonLd, language, location.pathname]);

  return null;
};